import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, Calendar, User, Tag, ArrowRight, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

function BlogCard({ post }) {
  const date = new Date(post.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  return (
    <Link to={`/blog/${post.slug}`} data-testid={`blog-card-${post.id}`} className="no-underline group">
      <div className="glass rounded-2xl overflow-hidden hover:-translate-y-2 transition-all duration-300 h-full flex flex-col"
        style={{ border: "1px solid rgba(0,243,255,0.1)" }}>
        {post.cover_image && (
          <div className="relative overflow-hidden h-44">
            <img src={post.cover_image} alt={post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              onError={(e) => { e.target.style.display = "none"; }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(3,3,5,0.8) 0%, transparent 50%)" }} />
          </div>
        )}
        <div className="p-5 flex flex-col flex-1">
          {/* Tags */}
          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-3">
              {post.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="text-xs font-outfit px-2 py-0.5 rounded-full"
                  style={{ background: "rgba(0,243,255,0.08)", color: "#00F3FF", border: "1px solid rgba(0,243,255,0.18)" }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
          <h2 className="font-rajdhani font-700 text-base text-white mb-2 leading-snug group-hover:text-cyan-300 transition-colors line-clamp-2">
            {post.title}
          </h2>
          <p className="font-outfit text-sm text-gray-500 leading-relaxed mb-4 flex-1 line-clamp-2" style={{ fontWeight: 300 }}>
            {post.excerpt}
          </p>
          <div className="flex items-center justify-between mt-auto pt-3 border-t" style={{ borderColor: "rgba(255,255,255,0.05)" }}>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 text-xs text-gray-600 font-outfit">
                <User size={10} /> {post.author}
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-600 font-outfit">
                <Calendar size={10} /> {date}
              </div>
            </div>
            <span className="text-xs font-rajdhani font-700 flex items-center gap-1" style={{ color: "#00F3FF" }}>
              READ <ArrowRight size={10} />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog`).then((r) => setPosts(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page" className="page-enter pt-24">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden"
        style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(188,19,254,0.08) 0%, transparent 60%), #030305" }}>
        <div className="animated-grid absolute inset-0 opacity-25" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 mb-5 px-4 py-2 rounded-full text-xs font-rajdhani font-700 tracking-widest"
            style={{ background: "rgba(188,19,254,0.08)", border: "1px solid rgba(188,19,254,0.25)", color: "#BC13FE" }}>
            <BookOpen size={12} /> INDIACLOUD BLOG
          </div>
          <h1 className="font-orbitron font-900 text-4xl lg:text-5xl text-white mb-4">
            Hosting Tips & <span className="text-gradient">Insights</span>
          </h1>
          <p className="font-outfit text-base text-gray-400" style={{ fontWeight: 300 }}>
            Guides, tutorials, and expert advice to help you get the most out of your hosting.
          </p>
        </div>
      </section>

      {/* Posts grid */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={24} color="#00F3FF" className="animate-spin" />
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-outfit text-gray-500">No blog posts yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((post) => <BlogCard key={post.id} post={post} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
